import React, { useState, useEffect } from 'react';
import FileUpload from '../components/FileUpload';
import {
  DocumentTextIcon,
  SparklesIcon,
  CheckCircleIcon,
  TrashIcon,
  PlusIcon
} from '@heroicons/react/24/outline';

export default function Categorization() {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [extractedFramework, setExtractedFramework] = useState(null);
  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    loadExistingFramework();
  }, []);

  const loadExistingFramework = async () => {
    try {
      const response = await fetch('/api/v1/zoomfabric/categorization/framework');
      if (response.ok) {
        const data = await response.json();
        if (data.framework) {
          setExtractedFramework(data.framework);
          setCategories(data.framework.categories || []);
        }
      }
    } catch (err) {
      console.error('Failed to load categorization framework:', err);
    }
  };

  const handleFileUpload = async (files) => {
    const fileArray = Array.isArray(files) ? files : [files];
    setUploadedFiles(prev => [...prev, ...fileArray]);

    // Auto-extract framework when files are uploaded
    extractFramework([...uploadedFiles, ...fileArray]);
  };

  const extractFramework = async (files) => {
    setLoading(true);
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await fetch('/api/v1/zoomfabric/categorization/extract', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setExtractedFramework(data.framework);
        setCategories(data.framework.categories || []);
      }
    } catch (err) {
      console.error('Failed to extract framework:', err);
    } finally {
      setLoading(false);
    }
  };

  const removeFile = (index) => {
    const newFiles = uploadedFiles.filter((_, i) => i !== index);
    setUploadedFiles(newFiles);

    if (newFiles.length > 0) {
      extractFramework(newFiles);
    } else {
      setExtractedFramework(null);
      setCategories([]);
    }
  };

  const addCategory = () => {
    const newCategory = {
      id: Date.now().toString(),
      name: '',
      description: '',
      keywords: [],
      confidence_threshold: 0.7
    };
    setCategories([...categories, newCategory]);
  };

  const updateCategory = (id, field, value) => {
    setCategories(categories.map(cat =>
      cat.id === id ? { ...cat, [field]: value } : cat
    ));
  };

  const deleteCategory = (id) => {
    setCategories(categories.filter(cat => cat.id !== id));
  };

  const saveFramework = async () => {
    setLoading(true);
    try {
      const framework = {
        ...extractedFramework,
        categories
      };

      const response = await fetch('/api/v1/zoomfabric/categorization/framework', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ framework })
      });

      if (response.ok) {
        alert('Categorization framework saved successfully!');
      }
    } catch (err) {
      console.error('Failed to save framework:', err);
      alert('Failed to save framework');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Call Categorization</h1>
        <p className="text-lg text-gray-600 mt-2">
          Upload guidance documents to configure how calls should be categorized
        </p>
      </div>

      {/* File Upload Section */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Upload Categorization Guidance</h3>
          <FileUpload onFileSelect={handleFileUpload} multiple={true} />

          {uploadedFiles.length > 0 && (
            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Uploaded Files:</h4>
              <div className="space-y-2">
                {uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between bg-gray-50 p-2 rounded">
                    <span className="text-sm text-gray-600">{file.name}</span>
                    <button
                      onClick={() => removeFile(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* AI Extracted Framework */}
      {loading && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6 text-center">
            <SparklesIcon className="w-8 h-8 text-blue-500 mx-auto mb-4 animate-pulse" />
            <p className="text-gray-600">AI is analyzing your guidance documents...</p>
          </div>
        </div>
      )}

      {extractedFramework && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Extracted Categorization Framework</h3>
              <button
                onClick={addCategory}
                className="flex items-center px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <PlusIcon className="w-4 h-4 mr-1" />
                Add Category
              </button>
            </div>

            {extractedFramework.description && (
              <div className="mb-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-blue-900">{extractedFramework.description}</p>
              </div>
            )}

            <div className="space-y-4">
              {categories.map((category, index) => (
                <div key={category.id || index} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-3">
                    <input
                      type="text"
                      value={category.name}
                      onChange={(e) => updateCategory(category.id || index, 'name', e.target.value)}
                      placeholder="Category name"
                      className="text-lg font-medium border-none bg-transparent focus:ring-0 focus:outline-none"
                    />
                    <button
                      onClick={() => deleteCategory(category.id || index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  </div>

                  <textarea
                    value={category.description}
                    onChange={(e) => updateCategory(category.id || index, 'description', e.target.value)}
                    placeholder="Category description"
                    rows={2}
                    className="w-full p-2 border border-gray-300 rounded-md text-sm"
                  />

                  <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Keywords (comma-separated)</label>
                      <input
                        type="text"
                        value={category.keywords?.join(', ') || ''}
                        onChange={(e) => updateCategory(category.id || index, 'keywords', e.target.value.split(',').map(k => k.trim()))}
                        placeholder="keyword1, keyword2, keyword3"
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Confidence Threshold</label>
                      <input
                        type="number"
                        min="0"
                        max="1"
                        step="0.1"
                        value={category.confidence_threshold || 0.7}
                        onChange={(e) => updateCategory(category.id || index, 'confidence_threshold', parseFloat(e.target.value))}
                        className="w-full p-2 border border-gray-300 rounded-md text-sm"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 flex justify-end">
              <button
                onClick={saveFramework}
                disabled={loading}
                className="bg-green-600 text-white px-6 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                <CheckCircleIcon className="w-4 h-4 mr-2" />
                {loading ? 'Saving...' : 'Save Framework'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="flex">
          <DocumentTextIcon className="w-6 h-6 text-gray-600 flex-shrink-0" />
          <div className="ml-3">
            <h3 className="text-lg font-medium text-gray-900 mb-2">How It Works</h3>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Upload documents that describe how calls should be categorized</li>
              <li>• AI will extract categories, keywords, and descriptions automatically</li>
              <li>• Review and edit the extracted framework to match your needs</li>
              <li>• Add custom categories or modify existing ones as needed</li>
              <li>• Save the framework to use in call analysis jobs</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}